import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cashSessionService, type ICashSessionStatus } from '@/services';
import CashSessionManager from './CashSessionManager';
import { formatCurrency, formatTime } from '@/lib/utils';
import { Clock, DollarSign, AlertTriangle } from 'lucide-react';

interface CashSessionStatusProps {
  className?: string;
  posId?: number; // ID del POS seleccionado
}

export const CashSessionStatus: React.FC<CashSessionStatusProps> = ({ className, posId }) => {
  const [sessionStatus, setSessionStatus] = useState<ICashSessionStatus>({ 
    isOpen: false, 
    sessionId: null, 
    cajeroId: null, 
    efectivoDeclarado: 0, 
    efectivoCalculado: 0, 
    inicioTurno: null 
  });
  
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    loadSessionStatus();
    
    // Actualizar cada 30 segundos
    const interval = setInterval(loadSessionStatus, 30000);
    
    // Escuchar cambios en localStorage
    const handleStorageChange = () => {
      loadSessionStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const loadSessionStatus = async () => {
    const status = cashSessionService.getCurrentSession();
    setSessionStatus(status);
  };

  const difference = sessionStatus.efectivoCalculado - sessionStatus.efectivoDeclarado;
  const hasDifference = Math.abs(difference) > 0;

  if (!sessionStatus.isOpen) {
    return (
      <Card className={`border-orange-200 bg-orange-50 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span className="font-medium text-orange-800">Sin Sesión de Caja</span>
            </div>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  Iniciar Sesión
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Gestión de Sesión de Caja</DialogTitle>
                </DialogHeader>
                <CashSessionManager 
                  posId={posId}
                  onSessionChange={(status) => {
                    setSessionStatus(status);
                    if (status.isOpen) {
                      setShowDialog(false);
                    }
                  }} 
                />
              </DialogContent>
            </Dialog>
          </div>
          <Alert className="mt-3 border-orange-200 bg-orange-50">
            <AlertDescription className="text-orange-800">
              Debe iniciar una sesión de caja antes de procesar ventas
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`border-green-200 bg-green-50 ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            <span className="font-medium text-green-800">Sesión Activa</span>
            <Badge variant="default" className="bg-green-600">
              ABIERTA
            </Badge>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline">
                Gestionar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Gestión de Sesión de Caja</DialogTitle>
              </DialogHeader>
              <CashSessionManager 
                posId={posId}
                onSessionChange={(status) => {
                  setSessionStatus(status);
                  if (!status.isOpen) {
                    setShowDialog(false);
                  }
                }} 
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>Inicio:</span>
            </div>
            <p className="font-medium">
              {sessionStatus.inicioTurno ? formatTime(sessionStatus.inicioTurno) : 'N/A'}
            </p>
          </div>
          
          <div>
            <p className="text-muted-foreground">Efectivo en Caja:</p>
            <p className="font-medium text-green-700">
              {formatCurrency(sessionStatus.efectivoCalculado)}
            </p>
          </div>
        </div>

        {hasDifference && (
          <div className="mt-3 p-2 rounded-md bg-yellow-50 border border-yellow-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-yellow-800">Diferencia:</span>
              <span className={`font-medium ${
                difference >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatCurrency(difference)}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CashSessionStatus;
